import { Component, OnInit, inject, signal } from '@angular/core';
import { ApiClientService } from '../../core/api/api-client.service';
import {
  CreateOrganizationRequest,
  OrganizationSummary,
  PageResponse
} from '../../core/models/portal.models';

const EMPTY_ORGANIZATION_FORM: CreateOrganizationRequest = {
  name: '',
  organizationType: 'CENTRAL',
  street: '',
  postalCode: '',
  city: '',
  country: 'Deutschland',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  statusValidUntil: null,
  advisor: '',
  status: 'ACTIVE'
};

@Component({
  selector: 'app-organizations-page',
  standalone: true,
  templateUrl: './organizations-page.component.html'
})
export class OrganizationsPageComponent implements OnInit {
  private readonly api = inject(ApiClientService);

  protected readonly organizations = signal<PageResponse<OrganizationSummary> | null>(null);
  protected readonly search = signal('');
  protected readonly status = signal('');
  protected readonly dialogOpen = signal(false);
  protected readonly editingId = signal<number | null>(null);
  protected readonly form = signal<CreateOrganizationRequest>({ ...EMPTY_ORGANIZATION_FORM });
  protected readonly saving = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly deleteError = signal<string | null>(null);

  ngOnInit(): void {
    this.load();
  }

  protected load(): void {
    this.deleteError.set(null);
    this.api.organizations(0, 100, this.search(), this.status()).subscribe((organizations) => this.organizations.set(organizations));
  }

  protected reset(): void {
    this.search.set('');
    this.status.set('');
    this.load();
  }

  protected openCreateDialog(): void {
    this.editingId.set(null);
    this.form.set({ ...EMPTY_ORGANIZATION_FORM });
    this.error.set(null);
    this.dialogOpen.set(true);
  }

  protected openEditDialog(organization: OrganizationSummary): void {
    this.editingId.set(organization.id);
    this.form.set(this.formFromOrganization(organization));
    this.error.set(null);
    this.dialogOpen.set(true);
  }

  protected closeDialog(): void {
    if (this.saving()) {
      return;
    }
    this.dialogOpen.set(false);
    this.error.set(null);
  }

  protected save(event: Event): void {
    event.preventDefault();
    const request = this.normalizedRequest();

    if (!request.name) {
      this.error.set('Bitte gib einen Organisationsnamen ein.');
      return;
    }

    this.saving.set(true);
    this.error.set(null);

    const id = this.editingId();
    const operation = id
      ? this.api.updateOrganization(id, request)
      : this.api.createOrganization(request);

    operation.subscribe({
      next: () => {
        this.saving.set(false);
        this.dialogOpen.set(false);
        this.load();
      },
      error: () => {
        this.saving.set(false);
        this.error.set('Die Organisation konnte nicht gespeichert werden.');
      }
    });
  }

  protected deleteOrganization(organization: OrganizationSummary): void {
    this.deleteError.set(null);

    if (!window.confirm(`Organisation "${organization.name}" wirklich löschen? Zugehörige Unternehmer, Fahrer und Benutzer werden entfernt.`)) {
      return;
    }

    this.api.deleteOrganization(organization.id).subscribe({
      next: () => this.load(),
      error: (error) => {
        const message = typeof error?.error === 'string'
          ? error.error
          : error?.error?.message || 'Die Organisation konnte nicht gelöscht werden.';
        this.deleteError.set(message);
      }
    });
  }

  protected updateForm(field: keyof CreateOrganizationRequest, event: Event): void {
    const value = (event.target as HTMLInputElement | HTMLSelectElement).value;
    this.form.update((current) => ({ ...current, [field]: value }));
  }

  protected setSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value);
  }

  protected setStatus(event: Event): void {
    this.status.set((event.target as HTMLSelectElement).value);
  }

  protected valueOrDash(value: string | null): string {
    return value?.trim() ? value : '-';
  }

  protected address(organization: OrganizationSummary): string {
    const parts = [organization.street, [organization.postalCode, organization.city].filter(Boolean).join(' ').trim()].filter(Boolean);
    return parts.length ? parts.join(', ') : '-';
  }

  protected typeLabel(type: OrganizationSummary['organizationType']): string {
    const labels: Record<OrganizationSummary['organizationType'], string> = {
      CENTRAL: 'Zentrale',
      ASSOCIATION: 'Verband',
      PARTNER: 'Partner',
      SINGLE_COMPANY: 'Einzelunternehmen'
    };
    return labels[type] ?? type;
  }

  protected statusLabel(status: OrganizationSummary['status']): string {
    const labels: Record<OrganizationSummary['status'], string> = {
      ACTIVE: 'Aktiv',
      TEST: 'Test',
      INACTIVE: 'Inaktiv'
    };
    return labels[status] ?? status;
  }

  private formFromOrganization(organization: OrganizationSummary): CreateOrganizationRequest {
    return {
      name: organization.name,
      organizationType: organization.organizationType,
      street: organization.street,
      postalCode: organization.postalCode,
      city: organization.city,
      country: organization.country ?? 'Deutschland',
      contactName: organization.contactName,
      contactEmail: organization.contactEmail,
      contactPhone: organization.contactPhone,
      statusValidUntil: organization.statusValidUntil,
      advisor: organization.advisor,
      status: organization.status
    };
  }

  private normalizedRequest(): CreateOrganizationRequest {
    const current = this.form();
    return {
      name: current.name.trim(),
      organizationType: current.organizationType ?? 'CENTRAL',
      street: this.clean(current.street),
      postalCode: this.clean(current.postalCode),
      city: this.clean(current.city),
      country: this.clean(current.country) ?? 'Deutschland',
      contactName: this.clean(current.contactName),
      contactEmail: this.clean(current.contactEmail),
      contactPhone: this.clean(current.contactPhone),
      statusValidUntil: this.clean(current.statusValidUntil),
      advisor: this.clean(current.advisor),
      status: current.status ?? 'ACTIVE'
    };
  }

  private clean(value: string | null | undefined): string | null {
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
  }
}
